/**
 * LP-SDK — PTEngine Landing Page Runtime SDK
 *
 * Entry point: auto-initializes on script load.
 * Reads window.__PT_CONFIG__ for page context.
 * API host and SDK version are injected at build time via tsup define.
 */

import { getConfig, VERSION } from './config';
import { SDK } from './core/sdk';
import { createResponsiveCleanup } from './modules/responsive-cleanup';
import { createEventTracker } from './modules/event-tracker';
import { createFormHandler } from './modules/form-handler';
import { createFormLifecycle } from './modules/form-lifecycle';
import { createClickAction } from './modules/click-action';
import { createPostalAutoFill } from './modules/postal-autofill';
import { createAnimation } from './modules/animation';
import { createPublicAPI } from './api/public-api';
import { injectStyle } from './utils/dom';
import { log } from './utils/logger';

(function bootstrap() {
  // Re-entry guard: if a second copy of the SDK loads on the same page, skip.
  if (window.__PT_SDK_LOADED__) {
    log('warn', 'pt-sdk already loaded (v' + (window.__PT_VERSION__ || '?') + '); skipping re-init');
    return;
  }

  const config = getConfig();
  if (!config) {
    log('warn', 'pt-sdk: no __PT_CONFIG__.pageId, skipping bootstrap');
    return;
  }

  window.__PT_SDK_LOADED__ = true;
  window.__PT_VERSION__ = VERSION;

  const sdk = new SDK(config);

  sdk.use(createResponsiveCleanup());
  sdk.use(createEventTracker());
  sdk.use(createFormHandler());
  sdk.use(createFormLifecycle());
  sdk.use(createClickAction());
  sdk.use(createPostalAutoFill());
  sdk.use(createAnimation());

  // Cursor pointer for non-<a> clickable elements (editor used to inject this).
  // [data-pt-href] = current contract; [href]:not(a) = legacy published pages.
  injectStyle('[data-pt-href],[href]:not(a){cursor:pointer}');

  // Expose public API
  const api = createPublicAPI(sdk);
  window.PT = api;

  sdk.emit('ready');
})();

// Re-export public types so consumers can `import type { PublicAPI } from 'lp-sdk'`.
export type {
  PTConfig,
  PTPlugin,
  PublicAPI,
  SDKModule,
  SDKContext,
  TrackEventConfig,
  TrackEventProperty,
  FormPayload,
  FormMeta,
  UTMParams,
  PostalAddress,
  EventCallback,
  DebugInfo,
  PreflightReport,
  PreflightIssue,
  PreviewActionDetail,
} from './types';
